import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  model,
  ModelSignal,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { DataService } from '@core/services';
import { HospitalReportsComponent } from '@modules/cy/patient/orcd/components/hospital-reports/hospital-reports.component';
import { LabReportsComponent } from '@modules/cy/patient/orcd/components/lab-reports/lab-reports.component';
import { MedicalImagesComponent } from '@modules/cy/patient/orcd/components/medical-images/medical-images.component';
import { MedicalReportsComponent } from '@modules/cy/patient/orcd/components/medical-reports/medical-reports.component';
import { ProgressSpinner } from 'primeng/progressspinner';
import { TranslateService, Translation } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'ncp-orcd',
  imports: [
    RouterOutlet,
    HospitalReportsComponent,
    LabReportsComponent,
    MedicalImagesComponent,
    MedicalReportsComponent,
    ProgressSpinner,
  ],
  templateUrl: './orcd.component.html',
  styleUrls: ['./orcd.component.scss'],
})
export class OrcdComponent implements OnInit, AfterViewInit {
  @ViewChild('parentDiv') public pDiv!: ElementRef<HTMLDivElement>;
  @ViewChild('stickyContentsTable') public sct!: ElementRef<HTMLUListElement>;

  protected readonly router = inject(Router);
  protected readonly dataService = inject(DataService);
  private cdref = inject(ChangeDetectorRef);

  protected headHeight: ModelSignal<number> = model<number>(0);
  protected headTop: ModelSignal<number> = model<number>(0);
  protected activeSection: string = 'hospital_reports';
  protected clickedSection: boolean = false;
  protected sctStyle: { [key: string]: string } = {};
  protected loading = signal<boolean>(true);

  private readonly translate = inject(TranslateService);
  protected translations: Translation = this.translate.instant('patient.orcd');

  private loadedSections = {
    hospital: false,
    lab: false,
    image: false,
    report: false,
  };

  @ViewChild('stickyContentsTable')
  set stickyContentsTable(el: ElementRef<HTMLUListElement>) {
    if (el) {
      el.nativeElement.setAttribute(
        'style',
        'top: ' +
          (this.dataService.getValueByKey('headerHeight') +
            this.dataService.getValueByKey('headerTop')) +
          'px;'
      );
      this.sct = el;
    }
  }

  ngOnInit(): void {
    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.translations = this.translate.instant('patient.orcd');
    });
    this.resetLoading();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.resetLoading();
      }
    });

    this.dataService.dataVar$.subscribe((dataVar) => {
      this.headHeight.set(dataVar.get('headerHeight'));
      this.headTop.set(dataVar.get('headerTop'));

      if (this.sct) {
        this.sctStyle['top'] =
          this.dataService.getValueByKey('headerHeight') +
          this.dataService.getValueByKey('headerTop') +
          'px';

        this.sct.nativeElement.setAttribute('style', this.objToStyleString(this.sctStyle));
      }

      this.cdref.detectChanges();
    });
  }

  ngAfterViewInit(): void {
    if (this.sct) {
      this.sctStyle['top'] =
        this.dataService.getValueByKey('headerHeight') +
        this.dataService.getValueByKey('headerTop') +
        'px';

      this.sct.nativeElement.setAttribute('style', this.objToStyleString(this.sctStyle));
    }

    this.cdref.detectChanges();
  }

  private resetLoading(): void {
    this.loading.set(true);

    this.loadedSections = {
      hospital: false,
      lab: false,
      image: false,
      report: false,
    };
  }

  protected onSectionLoaded(section: 'hospital' | 'lab' | 'image' | 'report'): void {
    this.loadedSections[section] = true;

    const allLoaded =
      this.loadedSections.hospital &&
      this.loadedSections.lab &&
      this.loadedSections.image &&
      this.loadedSections.report;

    if (allLoaded) {
      this.loading.set(false);
    }
  }

  protected objToStyleString = (obj: { [key: string]: string }): string => {
    let result = '';

    Object.keys(obj).forEach((key: string) => {
      if (obj.hasOwnProperty(key)) {
        result += key + ': ' + obj[key] + '; ';
      }
    });

    return result;
  };

  protected goTo = (el: HTMLHeadingElement): void => {
    this.activeSection = el.id;
    this.clickedSection = true;

    window.scrollTo(0, el.offsetTop - this.headHeight() - 10);
  };

  protected onScroll = (event: any, el: HTMLHeadingElement, content: HTMLDivElement): void => {
    if (
      el.getBoundingClientRect().top >
        this.headHeight() + this.headTop() + 10 - content.getBoundingClientRect().height &&
      el.getBoundingClientRect().top <= this.headHeight() + this.headTop() + 10
    ) {
      if (!this.clickedSection && event.type === 'scrollend') {
        this.activeSection = el.id;
      }

      if (event.type === 'scrollend') {
        this.clickedSection = false;
      }
    }
  };

  onResize(event: any, el: HTMLDivElement, type?: string): void {
    if (!this.pDiv || !this.sct) {
      return;
    }

    this.sctStyle['bottom'] =
      (this.pDiv.nativeElement.getBoundingClientRect().bottom < window.innerHeight
        ? window.innerHeight - this.pDiv.nativeElement.getBoundingClientRect().bottom - 10
        : 0) + 'px';

    this.sctStyle['overflow-y'] = 'auto';

    this.sct.nativeElement.setAttribute('style', this.objToStyleString(this.sctStyle));
  }
}
